import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import LoadingBox from '../../components/LoadingBox';
import { VesselService } from '../../util/services/vessel';
import { useSelector } from '../../util/store/store';
import { selectVessel } from '../../util/store/vessel/constants';
import { vesselActions } from '../../util/store/vessel/reducer';
import { showErrorResponse } from '../../util/varibles/global';
import DurationPicker from './DurationPicker';
import FleetOverview from './FleetOverview';
import { useVesselOverview } from './VesselOverviewContext';
import VesselOverviewHeader from './VesselOverviewHeader';

const VesselOverviewContent: React.FC = () => {
    const dispatch = useDispatch();
    const { data: vessels, loading: loadingVessels } = useSelector(selectVessel.vesselContracts);
    const [duration, setStore] = useVesselOverview(state => state.duration);
    const [loadingVesselIds] = useVesselOverview(state => state.loadingVesselIds);
    const [sumary] = useVesselOverview(state => state.sumary);
    const [search] = useVesselOverview(state => state.search);
    const [vesselFilter] = useVesselOverview(state => state.vesselFilter);
    const [vesselTypeFilter] = useVesselOverview(state => state.vesselTypeFilter);
    const [insightFilters] = useVesselOverview(state => state.insightFilters);
    const [appliedInsightFilters] = useVesselOverview(state => state.appliedInsightFilters);
    const hasActiveInsightFilters = useMemo(
        () => appliedInsightFilters.some(item => item.value != null),
        [appliedInsightFilters],
    );
    const selectedVessels = useMemo(() => {
        if (!vesselFilter.length) return vessels;

        const selectedIds = new Set(vesselFilter);
        return vessels.filter(vessel => selectedIds.has(vessel.id));
    }, [vesselFilter, vessels]);
    const selectedVesselIds = useMemo(
        () => selectedVessels.map(vessel => vessel.id),
        [selectedVessels],
    );
    const searchFilteredVessels = useMemo(() => {
        const searchTerm = search.trim().toLocaleLowerCase();
        if (!searchTerm) return selectedVessels;

        return selectedVessels.filter(vessel =>
            vessel.name.toLocaleLowerCase().includes(searchTerm)
        );
    }, [search, selectedVessels]);
    const typeFilteredVessels = useMemo(() => {
        if (!vesselTypeFilter.length) return searchFilteredVessels;

        const selectedTypes = new Set(vesselTypeFilter);
        return searchFilteredVessels.filter(vessel => selectedTypes.has(vessel.type));
    }, [searchFilteredVessels, vesselTypeFilter]);
    const summaryByVesselId = useMemo(
        () => new Map(sumary.map(item => [item.vessel_id, item])),
        [sumary],
    );
    const loadingIds = useMemo(
        () => new Set(loadingVesselIds),
        [loadingVesselIds],
    );
    const filterVesselsByInsight = useCallback((
        sourceVessels: typeof typeFilteredVessels,
        filters: typeof insightFilters,
        includeLoadingVessels = true,
    ) => {
        const validFilters = filters.filter(item => item.value != null);
        if (!validFilters.length || duration[1] <= duration[0]) return sourceVessels;

        const durationTime = duration[1] - duration[0];
        return sourceVessels.filter(vessel => {
            const vesselSummary = summaryByVesselId.get(vessel.id);
            if (!vesselSummary) {
                return includeLoadingVessels && loadingIds.has(vessel.id);
            }

            return validFilters.every(({ metric, operator, value }) => {
                const percentage = Number(vesselSummary[metric] || 0) / durationTime * 100;
                if (operator === "<") return percentage < value!;
                if (operator === ">") return percentage > value!;
                return Math.round(percentage) === value;
            });
        });
    }, [duration, loadingIds, summaryByVesselId]);
    const filteredVessels = useMemo(
        () => filterVesselsByInsight(typeFilteredVessels, appliedInsightFilters),
        [appliedInsightFilters, filterVesselsByInsight, typeFilteredVessels],
    );
    const insightMatchedVesselCount = useMemo(
        () => filterVesselsByInsight(typeFilteredVessels, insightFilters, false).length,
        [filterVesselsByInsight, insightFilters, typeFilteredVessels],
    );
    const filteredSumary = useMemo(() => {
        if (
            !search.trim()
            && !vesselFilter.length
            && !vesselTypeFilter.length
            && !hasActiveInsightFilters
        ) return sumary;

        const selectedIds = new Set(filteredVessels.map(vessel => vessel.id));
        return sumary.filter(item => selectedIds.has(item.vessel_id));
    }, [
        filteredVessels,
        hasActiveInsightFilters,
        search,
        sumary,
        vesselFilter,
        vesselTypeFilter,
    ]);

    useEffect(() => {
        dispatch(vesselActions.getVesselContracts());
    }, [dispatch]);

    const durationKeyRef = useRef(duration.join(':'));
    const requestVersionRef = useRef(0);
    const fetchedVesselIdsRef = useRef(new Set<string>());
    const loadingVesselIdsRef = useRef(new Set<string>());

    useEffect(() => {
        const durationKey = duration.join(':');
        const durationChanged = durationKeyRef.current !== durationKey;
        if (durationChanged) {
            durationKeyRef.current = durationKey;
            requestVersionRef.current += 1;
            fetchedVesselIdsRef.current.clear();
            loadingVesselIdsRef.current.clear();
        }

        const missingVesselIds = selectedVesselIds.filter(id =>
            !fetchedVesselIdsRef.current.has(id) &&
            !loadingVesselIdsRef.current.has(id)
        );
        if (!missingVesselIds.length) {
            if (durationChanged) {
                setStore({ sumary: [], loadingVesselIds: [] });
            }
            return;
        }

        const requestVersion = requestVersionRef.current;
        missingVesselIds.forEach(id => loadingVesselIdsRef.current.add(id));
        const nextLoadingVesselIds = Array.from(loadingVesselIdsRef.current);
        setStore(durationChanged
            ? { sumary: [], loadingVesselIds: nextLoadingVesselIds }
            : { loadingVesselIds: nextLoadingVesselIds }
        );

        VesselService.summary({
            vessel_ids: missingVesselIds,
            start_time: duration[0],
            finish_time: duration[1],
        })
            .then(newSumary => {
                if (requestVersion !== requestVersionRef.current) return;

                missingVesselIds.forEach(id => {
                    fetchedVesselIdsRef.current.add(id);
                    loadingVesselIdsRef.current.delete(id);
                });
                const updatedIds = new Set(newSumary.map(item => item.vessel_id));
                setStore(state => ({
                    sumary: [
                        ...state.sumary.filter(item => !updatedIds.has(item.vessel_id)),
                        ...newSumary,
                    ],
                    loadingVesselIds: Array.from(loadingVesselIdsRef.current),
                }));
            })
            .catch(error => {
                if (requestVersion !== requestVersionRef.current) return;

                missingVesselIds.forEach(id => loadingVesselIdsRef.current.delete(id));
                showErrorResponse('Get data failure', error);
                setStore({ loadingVesselIds: Array.from(loadingVesselIdsRef.current) });
            });
    }, [duration, selectedVesselIds, setStore]);

    return <div className='m-header bgPage pt-[20px] px-[40px]'>
        <VesselOverviewHeader vessels={vessels} loading={loadingVessels} />
        {loadingVessels
            ? <LoadingBox
                className='min-h-[calc(100vh-120px)]'
                loading
                level='page'
                spinnerSize={36}
            />
            : <>
                <DurationPicker />
                <FleetOverview
                    sumary={filteredSumary}
                    vessels={filteredVessels}
                    countVessels={searchFilteredVessels}
                    filteredCount={insightMatchedVesselCount}
                />
            </>
        }
    </div>;
};

export default VesselOverviewContent;
